import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { IconGripVertical } from '@tabler/icons-react'
import { memo, type ReactNode, useCallback, useMemo } from 'react'
import { cn } from '#src/cn.ts'

export interface ReorderItem {
  id: string
  component: ReactNode
}

export interface ReorderListProps {
  items: ReorderItem[]
  onChange: (orderedIds: string[]) => void
  className?: string
  dragHandle?: boolean
  // Merged over the grip's default classes, e.g. to fit it inside a narrower
  // gutter than its default 32px.
  dragHandleClassName?: string
}

export function ReorderList({
  items,
  onChange,
  className,
  dragHandle = false,
  dragHandleClassName,
}: ReorderListProps): ReactNode {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const itemIds = useMemo(() => items.map((item) => item.id), [items])

  const handleDragEnd = useCallback(
    (event: DragEndEvent): void => {
      const { active, over } = event

      if (!over || active.id === over.id) {
        return
      }

      const oldIndex = itemIds.indexOf(active.id as string)
      const newIndex = itemIds.indexOf(over.id as string)

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(itemIds, oldIndex, newIndex)
        onChange(newOrder)
      }
    },
    [itemIds, onChange]
  )

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <div className={cn('flex flex-col', className)}>
          {items.map((item) => (
            <SortableItem
              key={item.id}
              id={item.id}
              dragHandle={dragHandle}
              dragHandleClassName={dragHandleClassName}
            >
              {item.component}
            </SortableItem>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}

interface SortableItemProps {
  id: string
  children: ReactNode
  dragHandle: boolean
  dragHandleClassName?: string
}

const SortableItem = memo(function SortableItem({
  id,
  children,
  dragHandle,
  dragHandleClassName,
}: SortableItemProps): ReactNode {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = useMemo(
    () => ({
      // Use Translate instead of Transform to avoid scaleY causing height jitter
      transform: CSS.Translate.toString(transform),
      transition,
    }),
    [transform, transition]
  )

  if (dragHandle) {
    // Grip icon handle mode
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={cn(
          'group/draggable relative',
          isDragging && 'z-50 opacity-50'
        )}
      >
        <div
          {...attributes}
          {...listeners}
          className={cn(
            'absolute inset-y-0 right-full flex cursor-grab items-center justify-center px-2 opacity-0 transition-opacity group-hover/draggable:opacity-100 active:cursor-grabbing',
            dragHandleClassName
          )}
        >
          <IconGripVertical className="size-4 text-foreground-tertiary" />
        </div>
        {children}
      </div>
    )
  }

  // Whole item draggable mode (default) - cursor-default normally, cursor-grabbing when active
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'cursor-default active:cursor-grabbing',
        isDragging && 'z-50 opacity-50'
      )}
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  )
})
